import React, { useCallback, useEffect, useMemo, useRef, useState, useLayoutEffect } from "react";
import { ChessPuzzle, useChessPuzzleContext } from "@react-chess-puzzle-fix/react-chess-puzzle";
import { useChessGameContext } from "react-chess-game-fix";
import { adaptiveApi } from "../api";
import { ChessAnalysisPanel } from "../woodpecker/ChessAnalysisPanel";
import PuzzleBoard from '../components/puzzles/PuzzleBoard';
import PuzzleActions from '../components/puzzles/PuzzleActions';
import PuzzleWorkspace, { PuzzlePanel } from '../components/puzzles/PuzzleWorkspace';
import {
  MAX_RATING,
  MIN_RATING,
  getRangeDelta,
  updateRating,
} from "./rating";

// ----- Adaptive constants -----
const DEFAULT_BOARD_SIZE = typeof window !== "undefined" ? Math.min(window.innerHeight * 0.7, 600) : 520;
const INITIAL_RATING = 1200;
const CACHE_KEY_RATING = "adaptive:userRating";
const CACHE_KEY_ATTEMPTS = "adaptive:attempts";

// Buffer tuning
const HIGH_WATERMARK = 100; // target buffer size
const LOW_WATERMARK = 40; // when we drop below this, prefetch
const BASE_BATCH_SIZE = 60; // minimum puzzles to fetch in one call

function AdaptivePuzzlePage() {
  const [userRating, setUserRating] = useState(INITIAL_RATING);
  const [attempts, setAttempts] = useState(0);
  const [cacheReady, setCacheReady] = useState(false);

  const [buffer, setBuffer] = useState([]);
  const [activePuzzle, setActivePuzzle] = useState(null);
  const [boardSize, setBoardSize] = useState(DEFAULT_BOARD_SIZE);
  const [wrongMove, setWrongMove] = useState(null);

  const [isPrefetching, setIsPrefetching] = useState(false);
  const [prefetchError, setPrefetchError] = useState(null);
  const [lastPrefetchCenter, setLastPrefetchCenter] = useState(INITIAL_RATING);


  // ----- Range helpers -----

  const currentRange = useMemo(() => {
    const delta = getRangeDelta(attempts);
    return {
      min: Math.max(MIN_RATING, userRating - delta),
      max: Math.min(MAX_RATING, userRating + delta),
    };
  }, [userRating, attempts]);

  const currentRangeWindowSize = currentRange.max - currentRange.min;
  // If rating comes within 25 points of either window edge, reset buffer
  const RATING_RESET_THRESHOLD = currentRangeWindowSize / 2 - 25;

  // ----- Cached state -----

  useEffect(() => {
    if (typeof window === "undefined") return;

    const cachedRating = window.localStorage.getItem(CACHE_KEY_RATING);
    const cachedAttempts = window.localStorage.getItem(CACHE_KEY_ATTEMPTS);

    if (cachedRating !== null) {
      const parsed = Number(cachedRating);
      if (Number.isFinite(parsed)) setUserRating(parsed);
    }

    if (cachedAttempts !== null) {
      const parsed = Number(cachedAttempts);
      if (Number.isFinite(parsed)) setAttempts(parsed);
    }

    setCacheReady(true);
  }, []);

  useEffect(() => {
    if (!cacheReady || typeof window === "undefined") return;
    window.localStorage.setItem(CACHE_KEY_RATING, String(userRating));
    window.localStorage.setItem(CACHE_KEY_ATTEMPTS, String(attempts));
  }, [cacheReady, userRating, attempts]);

  // ----- Prefetch logic -----

  const prefetchPuzzles = useCallback(
    async (desiredCount) => {
      if (isPrefetching) return;
      setIsPrefetching(true);
      setPrefetchError(null);

      const { min, max } = currentRange;
      const targetCount = desiredCount ?? Math.max(BASE_BATCH_SIZE, HIGH_WATERMARK - buffer.length);

      try {
        const puzzles = await adaptiveApi.fetchFreeplayPuzzles({
          minRating: min,
          maxRating: max,
          count: targetCount,
        });

        console.log(`Prefetched ${puzzles.length} puzzles for rating range ${min}-${max}`);
        console.log(puzzles);
        setBuffer((prev) => {
          const existingIds = new Set(prev.map((p) => p.puzzleId));
          const dedupedNew = puzzles.filter((p) => !existingIds.has(p.puzzleId));
          return [...dedupedNew, ...prev];
        });
        setLastPrefetchCenter((min + max) / 2);
      } catch (err) {
        console.error(err);
        setPrefetchError(err.message ?? "Failed to prefetch puzzles");
      } finally {
        setIsPrefetching(false);
      }
    },
    [buffer.length, currentRange, isPrefetching]
  );

  // Startup: ensure we have a warm buffer and an active puzzle
  useEffect(() => {
    if (buffer.length === 0 && !isPrefetching) {
      prefetchPuzzles().catch(() => {});
    }
  }, [buffer.length, isPrefetching, prefetchPuzzles]);

  // If rating moves too far from last prefetch center, reset buffer
  useEffect(() => {
    if (Math.abs(userRating - lastPrefetchCenter) > RATING_RESET_THRESHOLD && buffer.length > 0) {
      setBuffer([]);
      // Prefetch will be triggered by the other effect when buffer length hits 0
    }
  }, [userRating, lastPrefetchCenter, buffer.length, RATING_RESET_THRESHOLD]);

  // Keep buffer topped up when it gets low
  useEffect(() => {
    if (buffer.length <= LOW_WATERMARK && !isPrefetching) {
      prefetchPuzzles().catch(() => {});
    }
  }, [buffer.length, isPrefetching, prefetchPuzzles]);

  // When buffer changes and we don't have an active puzzle, pull one
  useEffect(() => {
    if (!activePuzzle && buffer.length > 0) {
      setActivePuzzle(buffer[buffer.length - 1]); // pop last
      setBuffer((prev) => prev.slice(0, prev.length - 1));
    }
  }, [activePuzzle, buffer]);

  // ----- Handlers -----

  const handleNextPuzzle = useCallback(() => {
    if (buffer.length === 0) {
      // In the rare case the buffer is empty, just clear activePuzzle.
      // Prefetch effects will kick in and fill it again.
      setActivePuzzle(null);
      return;
    }
    setActivePuzzle(buffer[buffer.length - 1]);
    setBuffer((prev) => prev.slice(0, prev.length - 1));
  }, [buffer]);

  const handlePuzzleSolved = useCallback(
    (correct) => {
      if (!activePuzzle) return;

      const newAttempts = attempts + 1;
      const newRating = updateRating(userRating, activePuzzle.rating, correct, attempts);

      setAttempts(newAttempts);
      setUserRating(newRating);
    },
    [activePuzzle, attempts, userRating]
  );

  const workspace = (
    <PuzzleWorkspace
      title="Adaptive puzzles"
      subtitle="Puzzles that adapt to your level."
      sidebar={
        <>
          <PuzzlePanel title="Your progress">
            <dl className="grid grid-cols-2 gap-4">
              <div><dt className="text-sm text-slate-600">Rating</dt><dd className="mt-1 text-2xl font-semibold">{userRating}</dd></div>
              <div><dt className="text-sm text-slate-600">Puzzles solved</dt><dd className="mt-1 text-2xl font-semibold">{attempts}</dd></div>
            </dl>
            {prefetchError && <p role="alert" className="mt-4 text-sm text-red-700">{prefetchError}</p>}
            <div className="mt-5 hidden sm:block">
              <label htmlFor="adaptive-board-size-slider" className="text-sm font-medium">Board size</label>
              <input id="adaptive-board-size-slider" type="range" min="320" max="700" step="10" value={boardSize}
                onChange={(e) => setBoardSize(Number(e.target.value))} className="mt-2 w-full accent-sky-700" />
            </div>
          </PuzzlePanel>
          {activePuzzle && <ChessAnalysisPanel wrongMove={wrongMove} isPremium={true} puzzleIndex={attempts} />}
        </>
      }
    >
      {activePuzzle ? (
        <PuzzleBoardContent onSolved={handlePuzzleSolved} handleNextPuzzle={handleNextPuzzle}
          boardSize={boardSize} setWrongMove={setWrongMove} />
      ) : (
        <p role="status" className="px-4 py-16 text-center text-slate-600">
          {isPrefetching ? "Loading puzzles…" : "No puzzle available. Click reload or try again."}
        </p>
      )}
    </PuzzleWorkspace>
  );

  return activePuzzle ? <ChessPuzzle.Root puzzle={activePuzzle} key={activePuzzle.puzzleId}>{workspace}</ChessPuzzle.Root> : workspace;
}

export default AdaptivePuzzlePage;

function PuzzleBoardContent({ onSolved, handleNextPuzzle, boardSize, setWrongMove }) {
  const { status, autoRetry, currentMoveIndex } = useChessPuzzleContext();
  const { methods, game, currentFen } = useChessGameContext();
  const solvedRef = useRef(false);
  const failedRef = useRef(false);

  useEffect(() => {
    setWrongMove(null);
  }, [currentMoveIndex, setWrongMove]);

  useEffect(() => {
    if (status === "solved") {
      setWrongMove?.(null);
      return;
    }

    if (status !== "failed") return;

    const retryTimer = setTimeout(() => {
      autoRetry();
      methods?.undoMove();
      setWrongMove(game?.history()[game.history().length - 1]);
    }, 1000);

    return () => clearTimeout(retryTimer);
  }, [status, autoRetry, methods, game, currentFen, setWrongMove]);

  useLayoutEffect(() => {
    if (status === "failed") {
      failedRef.current = true;
    }
    if (status === "solved" && !solvedRef.current) {
      solvedRef.current = true;
      onSolved?.(!failedRef.current);
    }
  }, [status, onSolved]);

  return (
    <>
      <PuzzleBoard boardSize={boardSize} />
      <PuzzleActions showNext={status === 'solved'} onNext={handleNextPuzzle} />
    </>
  );
}
